package org.wordsapp.viewmodel

import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import org.wordsapp.currentTimeMillis
import org.wordsapp.data.WordRepository
import org.wordsapp.data.getDemoWords
import org.wordsapp.getPreference
import org.wordsapp.model.*
import org.wordsapp.setPreference
import kotlin.random.Random

data class LastResult(
    val correct: Boolean,
    val foreignWord: String,
    val translation: String,
    val spellingHint: String? = null,
    val missingArticle: Boolean = false,
)

private val GERMAN_ARTICLES = setOf("der", "die", "das")

class WordViewModel(private val repository: WordRepository) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)

    private val _words = MutableStateFlow<List<Word>>(emptyList())
    val words: StateFlow<List<Word>> = _words.asStateFlow()

    private val _currentWord = MutableStateFlow<Word?>(null)
    val currentWord: StateFlow<Word?> = _currentWord.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage: StateFlow<String?> = _errorMessage.asStateFlow()

    private val _lastResult = MutableStateFlow<LastResult?>(null)
    val lastResult: StateFlow<LastResult?> = _lastResult.asStateFlow()

    private val _roundCompleted = MutableStateFlow(false)
    val roundCompleted: StateFlow<Boolean> = _roundCompleted.asStateFlow()

    private val _authState = MutableStateFlow<AuthState>(AuthState.Loading)
    val authState: StateFlow<AuthState> = _authState.asStateFlow()

    private val _totalRoundWords = MutableStateFlow(0)
    val totalRoundWords: StateFlow<Int> = _totalRoundWords.asStateFlow()

    private val _answeredInRound = MutableStateFlow(0)
    val answeredInRound: StateFlow<Int> = _answeredInRound.asStateFlow()

    private val _hasSpreadsheet = MutableStateFlow<Boolean?>(null)
    val hasSpreadsheet: StateFlow<Boolean?> = _hasSpreadsheet.asStateFlow()

    private val _ignoreDiacritics = MutableStateFlow(getPreference("ignoreDiacritics") == "true")
    val ignoreDiacritics: StateFlow<Boolean> = _ignoreDiacritics.asStateFlow()

    private val _completedRounds = MutableStateFlow(0)
    val completedRounds: StateFlow<Int> = _completedRounds.asStateFlow()

    private val _reviewWord = MutableStateFlow<Word?>(null)
    val reviewWord: StateFlow<Word?> = _reviewWord.asStateFlow()

    private val _reviewRevealed = MutableStateFlow(false)
    val reviewRevealed: StateFlow<Boolean> = _reviewRevealed.asStateFlow()

    private val _reviewedCount = MutableStateFlow(0)
    val reviewedCount: StateFlow<Int> = _reviewedCount.asStateFlow()

    private var roundWordsList = mutableListOf<Word>()

    fun getLoginUrl(): String = repository.getLoginUrl()

    fun checkAuth() {
        scope.launch {
            try {
                val status = repository.checkAuth()
                _authState.value = if (status.authenticated) {
                    AuthState.Authenticated(status.email)
                } else {
                    AuthState.NotAuthenticated
                }
                if (status.authenticated) {
                    val settings = repository.getSettings()
                    _hasSpreadsheet.value = settings.spreadsheetId != null
                    _ignoreDiacritics.value = settings.ignoreDiacritics
                    setPreference("ignoreDiacritics", settings.ignoreDiacritics.toString())
                }
            } catch (_: Exception) {
                _authState.value = AuthState.NotAuthenticated
            }
        }
    }

    /**
     * Mobile login: exchanges the one-time token from the wordsapp://auth deep link
     * for a session cookie, then re-runs the normal auth check.
     */
    fun completeAppLogin(token: String) {
        scope.launch {
            try {
                val status = repository.exchangeAppSessionToken(token)
                if (status.authenticated) {
                    checkAuth()
                } else {
                    _errorMessage.value = "Login failed: token expired, please try again"
                }
            } catch (e: Exception) {
                _errorMessage.value = "Login failed: ${e.message}"
            }
        }
    }

    fun enterDemoMode() {
        _authState.value = AuthState.Demo
        _hasSpreadsheet.value = true
        _words.value = getDemoWords()
        startNewRound()
        ensureReviewStarted()
    }

    fun loadWords() {
        scope.launch {
            _isLoading.value = true
            _errorMessage.value = null
            try {
                _words.value = repository.fetchWords()
                startNewRound()
            } catch (e: Exception) {
                _errorMessage.value = e.message ?: "Failed to load words"
                _words.value = getDemoWords()
                startNewRound()
            } finally {
                _isLoading.value = false
            }
            // Words may have arrived after the Review tab already tried to pick a card
            ensureReviewStarted()
        }
    }

    fun startNewRound() {
        val available = _words.value
        if (available.isEmpty()) return

        roundWordsList = selectWeightedWords(available, minOf(5, available.size)).toMutableList()
        _totalRoundWords.value = roundWordsList.size
        _answeredInRound.value = 0
        _roundCompleted.value = false
        _lastResult.value = null
        advanceToNextWord()
    }

    /**
     * Returns true if the answer was scored (correct or incorrect) and the word advanced.
     * Returns false if we're prompting the user for a missing article — caller should
     * keep the input field intact so the user can prepend the article.
     */
    fun checkAnswer(userAnswer: String): Boolean {
        val word = _currentWord.value ?: return true
        val trimmed = userAnswer.trim()

        val expectedArticle = leadingArticle(word.foreignWord)
        if (expectedArticle != null && leadingArticle(trimmed) == null) {
            val nounOnly = word.foreignWord.substring(expectedArticle.length).trimStart()
            if (matchesForeignWord(trimmed, nounOnly)) {
                _lastResult.value = LastResult(
                    correct = false,
                    foreignWord = word.foreignWord,
                    translation = word.translation,
                    missingArticle = true,
                )
                return false
            }
        }

        val exactMatch = trimmed.equals(word.foreignWord, ignoreCase = true)
        val correct = matchesForeignWord(trimmed, word.foreignWord)
        val spellingHint = if (correct && !exactMatch) word.foreignWord else null

        _lastResult.value = LastResult(
            correct = correct,
            foreignWord = word.foreignWord,
            translation = word.translation,
            spellingHint = spellingHint,
        )

        val now = currentTimeMillis()
        val updated = word.review(if (correct) ReviewGrade.GOOD else ReviewGrade.AGAIN, now)

        _words.value = _words.value.map { if (it.id == updated.id) updated else it }

        if (correct) {
            _answeredInRound.value++
        } else {
            roundWordsList.add(updated)
        }

        advanceToNextWord()

        scope.launch {
            try {
                repository.updateStatistics(updated)
            } catch (_: Exception) {
                // Stats update failed silently — local state is still correct
            }
        }
        return true
    }

    /** Picks the first review card if none is shown yet (tab opened, or words just loaded). */
    fun ensureReviewStarted() {
        if (_reviewWord.value == null && _words.value.isNotEmpty()) pickNextReviewWord()
    }

    fun revealReviewAnswer() {
        _reviewRevealed.value = true
    }

    fun gradeReviewWord(grade: ReviewGrade) {
        val word = _reviewWord.value ?: return
        val updated = word.review(grade, currentTimeMillis())
        _words.value = _words.value.map { if (it.id == updated.id) updated else it }
        _reviewedCount.value++

        scope.launch {
            try {
                repository.updateStatistics(updated)
            } catch (_: Exception) {
                // Stats update failed silently — local state is still correct
            }
        }
        pickNextReviewWord(excludeId = updated.id)
    }

    private fun pickNextReviewWord(excludeId: Int? = null) {
        val all = _words.value
        val pool = all.filter { it.id != excludeId }.ifEmpty { all }
        _reviewWord.value = selectWeightedWords(pool, 1).firstOrNull()
        _reviewRevealed.value = false
    }

    fun setSpreadsheetUrl(url: String) {
        scope.launch {
            try {
                repository.saveSpreadsheetUrl(url)
                _hasSpreadsheet.value = true
                loadWords()
            } catch (e: Exception) {
                _errorMessage.value = "Failed to set spreadsheet: ${e.message}"
            }
        }
    }

    fun logout() {
        scope.launch {
            try {
                repository.logout()
            } catch (_: Exception) {
            }
            _authState.value = AuthState.NotAuthenticated
            _words.value = emptyList()
            _currentWord.value = null
            _reviewWord.value = null
            _reviewRevealed.value = false
        }
    }

    fun setIgnoreDiacritics(ignore: Boolean) {
        _ignoreDiacritics.value = ignore
        setPreference("ignoreDiacritics", ignore.toString())
        scope.launch {
            try {
                repository.saveIgnoreDiacritics(ignore)
            } catch (_: Exception) {
                // Fire-and-forget — local state is already updated
            }
        }
    }

    fun clearError() {
        _errorMessage.value = null
    }

    fun forceCompleteRound() {
        roundWordsList.clear()
        _lastResult.value = null
        completeRound()
    }

    private fun advanceToNextWord() {
        if (roundWordsList.isEmpty()) {
            completeRound()
        } else {
            _currentWord.value = roundWordsList.removeFirst()
        }
    }

    private fun completeRound() {
        _currentWord.value = null
        _roundCompleted.value = true
        _completedRounds.value++
    }

    private fun matchesForeignWord(input: String, expected: String): Boolean {
        return if (_ignoreDiacritics.value) {
            stripDiacritics(input).equals(stripDiacritics(expected), ignoreCase = true)
        } else {
            input.equals(expected, ignoreCase = true)
        }
    }

    private fun leadingArticle(text: String): String? {
        val first = text.trim().substringBefore(' ')
        return if (first.lowercase() in GERMAN_ARTICLES && first.length < text.trim().length) first else null
    }

    private fun stripDiacritics(text: String): String = buildString {
        for (ch in text) {
            when (ch) {
                'ä', 'Ä' -> append('a')
                'ö', 'Ö' -> append('o')
                'ü', 'Ü' -> append('u')
                'é' -> append('e')
                'ß' -> append("ss")
                else -> append(ch)
            }
        }
    }

    /**
     * Due words (including new ones) are drawn strictly before not-yet-due ones —
     * otherwise a crowd of low-weight scheduled-out words collectively outdraws
     * the few words that are actually due. Weights only matter within each tier.
     */
    private fun selectWeightedWords(words: List<Word>, count: Int): List<Word> {
        val now = currentTimeMillis()
        val (due, notDue) = words.partition { it.isDue(now) }
        val duePool = due.toMutableList()
        val notDuePool = notDue.toMutableList()

        val selected = mutableListOf<Word>()
        repeat(count) {
            val pool = if (duePool.isNotEmpty()) duePool else notDuePool
            if (pool.isEmpty()) return@repeat
            selected.add(drawWeighted(pool, now))
        }
        return selected
    }

    /** Removes and returns one word from the pool, chosen with probability proportional to weight. */
    private fun drawWeighted(pool: MutableList<Word>, now: Long): Word {
        val totalWeight = pool.sumOf { it.weight(now) }
        var random = Random.nextDouble() * totalWeight
        for (word in pool) {
            random -= word.weight(now)
            if (random <= 0) {
                pool.remove(word)
                return word
            }
        }
        return pool.removeAt(pool.lastIndex) // floating-point tail
    }
}
